import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import axiosInstance from '../utils/axiosInstance';
import Container from 'react-bootstrap/Container';
import AppFooter from "../components/Footer.js";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Pagination from 'react-bootstrap/Pagination';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import {
    FiSearch, FiFilter, FiTag, FiBarChart2, FiExternalLink, FiLogOut,
    FiImage, FiChevronLeft, FiChevronRight, FiStar, FiHeart, FiCheckCircle, FiUser, FiGrid
} from 'react-icons/fi';
import { getCourseImage } from '../utils/imgs.js';
import NavDropdown from "react-bootstrap/NavDropdown";
import { Link } from "react-router-dom";
import SiteNavbar from "../components/SiteNavbar";

import { BASE_URL } from '../utils/index.js'

// --- Brand Colors ---
const brandColors = {
    primary: 'var(--primary-color)', // Main interactive green
    primaryDark: 'var(--primary-color-dark)', // Darker shade for hover/active
    secondary: 'var(--secondary-color)', // Added
    secondaryDark: 'var(--secondary-color-dark)', // Added
    backgroundLight: 'var(--bg-light)', // Light green background
    backgroundWhite: 'var(--bg-white)', // Off-white background
    textPrimary: 'var(--text-primary)',
    textSecondary: 'var(--text-secondary)',
    textWhite: '#ffffff', // For header
    greyLight: 'var(--border-color)',
    greyMid: '#ced4da',
    success: '#28a745',
    warning: '#ffc107',
    starColor: '#ffc107', // Yellow for stars
};

// --- Styled Components ---

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${brandColors.backgroundLight}; // Light green is main background
    color: ${brandColors.textPrimary};
  }
`;

// const StyledFooter = styled.footer`
//   background-color: ${brandColors.backgroundWhite}; // Light footer
//   color: ${brandColors.textSecondary};
//   text-align: center;
//   padding: 1.5rem 0;
//   font-size: 0.9rem;
//   border-top: 1px solid ${brandColors.greyLight};
//   margin-top: auto; // Push the footer to the bottom
// `;

// --- Placeholder SearchAutocomplete Component ---
const SearchAutocomplete = ({ onSearch, initialQuery }) => {
    const [query, setQuery] = useState(initialQuery || '');

    const handleInputChange = (event) => {
        setQuery(event.target.value);
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();
        onSearch(query); // Trigger search on submit
    };

    return (
        <form onSubmit={handleFormSubmit} style={{ position: 'relative', width: '100%', maxWidth: '500px', minWidth: 0 }}>
            <SearchInput
                type="search"
                placeholder="Search for courses, skills, or keywords..."
                value={query}
                onChange={handleInputChange}
            />
            <SearchButton type="submit" aria-label="Search">
                <FiSearch />
            </SearchButton>
        </form>
    );
};
// --- End Placeholder SearchAutocomplete ---

const SearchInput = styled.input`
    padding: 1rem 1.5rem;
    border-radius: 50px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    color: ${brandColors.textWhite};
    min-width: 0;
    max-width: 600px;
    width: 100%;
    font-size: 1rem;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    transition: var(--transition-fast);

    &::placeholder {
        color: rgba(255, 255, 255, 0.8);
    }

    &:focus {
        outline: none;
        background: rgba(255, 255, 255, 0.25);
        border-color: rgba(255, 255, 255, 0.5);
        box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.2);
    }
`;
const SearchButton = styled.button`
    background: ${brandColors.secondary};
    border: none;
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    color: ${brandColors.textWhite};
    font-size: 1.2rem;
    cursor: pointer;
    padding: 10px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: var(--transition-fast);
    
    &:hover {
        background: ${brandColors.secondaryDark};
        transform: translateY(-50%) scale(1.05);
    }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const ExploreHeader = styled.div`
  background: linear-gradient(135deg, ${brandColors.primary} 0%, #008a6d 40%, ${brandColors.secondary} 100%);
  background-size: 200% 200%;
  animation: ${gradientAnimation} 12s ease infinite;
  color: ${brandColors.textWhite};
  padding: calc(var(--navbar-height) + 4.5rem) 1rem 4.5rem;
  margin-bottom: 2rem;
  box-shadow: inset 0 -10px 20px -10px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;

  /* Subtle background pattern */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 90%, rgba(255,255,255,0.1) 0%, transparent 40%);
    pointer-events: none;
  }

  h1 {
    font-size: clamp(1.85rem, 5vw, 3.5rem);
    font-weight: 800;
    margin-bottom: 1.2rem;
    letter-spacing: -0.02em;
    position: relative;
    z-index: 1;
  }

  p {
    font-size: clamp(1rem, 2vw, 1.25rem);
    margin-bottom: 2.5rem;
    max-width: 700px;
    line-height: 1.6;
    opacity: 0.95;
    position: relative;
    z-index: 1;
    padding: 0 8px;
  }

  @media (max-width: 768px) {
    padding: calc(var(--navbar-height) + 2rem) 1rem 2rem;

    h1 { margin-bottom: 0.75rem; }
    p { margin-bottom: 1.5rem; }
  }
  
  form {
    z-index: 1;
  }
`;

const ExploreContainer = styled(Container)`
  /* Container now doesn't need background, inherits from body */
  padding-top: 2.5rem;
  padding-bottom: 4rem;
`;

// Filters now use off-white background for contrast and sticky scroll behavior
const FilterWrapper = styled(Card)`
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 1.25rem 1.5rem;
  margin-bottom: 2.5rem;
  border: 1px solid rgba(0, 170, 135, 0.15);
  border-radius: var(--border-radius-lg);
  box-shadow: ${props => props.isNavHidden ? '0 12px 32px rgba(0, 0, 0, 0.12)' : '0 8px 30px rgba(0, 0, 0, 0.04)'};
  position: sticky;
  top: ${props => props.isNavHidden ? '12px' : 'calc(var(--navbar-height) + 12px)'};
  z-index: 400;
  transition: top 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, background-color 0.3s ease;

  .form-label {
    font-size: 0.85rem;
    font-weight: 700;
    color: ${brandColors.textSecondary};
    margin-bottom: 0.4rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  @media (max-width: 768px) {
    position: static;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const formControlBase = css`
    border-radius: 0.25rem;
    border: 1px solid ${brandColors.greyMid}; /* Ensure border is visible on white bg */
    background-color: ${brandColors.backgroundWhite}; /* Ensure bg is white */
    color: ${brandColors.textPrimary};
    font-size: 0.9rem;
    &:focus {
        border-color: ${brandColors.primary};
        box-shadow: 0 0 0 0.2rem rgba(89, 180, 154, 0.25);
    }
`;

const StyledFormSelect = styled(Form.Select)`
    ${formControlBase}
`;

const StyledFormControl = styled(Form.Control)`
    ${formControlBase}
`;

const StyledButton = styled(Button)`
  background-color: ${brandColors.primary};
  border-color: ${brandColors.primary};
  color: ${brandColors.textWhite}; // White text on primary button
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  text-decoration: none !important;
  transition: all 0.2s ease-in-out;

  &:hover, &:focus, &:active {
    background-color: ${brandColors.primaryDark};
    border-color: ${brandColors.primaryDark};
    color: ${brandColors.textWhite};
    text-decoration: none !important;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  &.btn-outline-primary {
      color: ${brandColors.primary};
      border-color: ${brandColors.primary};
      background-color: transparent;
      text-decoration: none !important;

       &:hover, &:focus, &:active {
            background-color: ${brandColors.backgroundLight}; // Use light green for hover bg
            color: ${brandColors.primaryDark};
            border-color: ${brandColors.primaryDark};
            text-decoration: none !important;
       }
  }

  &.btn-outline-danger {
    color: #dc3545; // Red color for text
    border-color: #dc3545; // Red border
    background-color: transparent; // Transparent background
    text-decoration: none !important;

    &:hover, &:focus, &:active {
        background-color: rgba(220, 53, 69, 0.1); // Light red on hover
        color: #dc3545;
        border-color: #dc3545;
        text-decoration: none !important;
    }
  }
`;

// Course cards use off-white background
const CourseCardWrapper = styled(Card)`
  border: none;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  transition: var(--transition-fast);
  height: 100%;
  background-color: ${brandColors.backgroundWhite};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 30px rgba(0, 170, 135, 0.15);
    
    /* Target the image placeholder inside to scale it on hover */
    .card-img-placeholder img {
        transform: scale(1.08);
    }
  }
`;

const CardImagePlaceholder = styled.div`
  height: 200px;
  background-color: ${brandColors.greyLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${brandColors.greyMid};
  font-size: 2.5rem;
  overflow: hidden;

  img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
`;

const CardBodyStyled = styled(Card.Body)`
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CardTitleStyled = styled(Card.Title)`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${brandColors.textPrimary};
  margin-bottom: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 2.6em;
`;

const CardDescription = styled(Card.Text)`
  font-size: 0.85rem; // Slightly smaller description
  color: ${brandColors.textSecondary};
  /* flex-grow: 1; // Removed flex-grow to allow content below to push up */
  margin-bottom: 0.75rem; // Reduced margin
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMetaInfo = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    font-size: 0.85rem;
    color: ${brandColors.textSecondary};
`;

const RatingStars = styled.div`
    display: inline-flex;
    align-items: center;
    color: ${brandColors.starColor};
    gap: 0.1rem;
     .filled-star { fill: ${brandColors.starColor}; }
     .empty-star { fill: ${brandColors.greyMid}; opacity: 0.5;}
`;

const PriceInfo = styled.span`
    font-weight: 600;
    color: ${brandColors.primary}; // Highlight price

    &.free {
        color: ${brandColors.success}; // Green for Free
    }
`;


const BadgeGroup = styled.div`
  margin-bottom: 1.2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StyledBadge = styled(Badge)`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.4em 0.7em;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 6px;

  &.badge-level { 
      background-color: rgba(0, 170, 135, 0.1) !important; 
      color: ${brandColors.primary}; 
  }
  &.badge-platform { 
      background-color: rgba(255, 122, 89, 0.1) !important; 
      color: ${brandColors.secondary}; 
  }
  &.badge-tag { 
      background-color: #f1f5f9 !important; 
      color: ${brandColors.textSecondary}; 
  }
`;

const CardFooterStyled = styled.div`
    margin-top: auto; // Pushes footer to bottom
    padding-top: 0.5rem; // Space above button
`;

// Pagination and Loading/Error styles remain largely the same but inherit body background
const PaginationWrapper = styled.div`
    /* Styles remain the same */
    margin-top: 2.5rem;
    display: flex;
    justify-content: center;

    .page-item .page-link {
        color: ${brandColors.primary};
        border-color: ${brandColors.greyLight};
        background-color: ${brandColors.backgroundWhite}; /* Ensure links have white bg */
        margin: 0 0.2rem;
        border-radius: 0.25rem;
        transition: all 0.2s ease-in-out;

        &:hover {
            background-color: ${brandColors.backgroundLight}; /* Light green on hover */
            border-color: ${brandColors.primary};
            z-index: 2;
        }

        &:focus {
             box-shadow: 0 0 0 0.2rem rgba(89, 180, 154, 0.25);
        }
    }

    .page-item.active .page-link {
        background-color: ${brandColors.primary};
        border-color: ${brandColors.primary};
        color: white;
        z-index: 3;
    }

     .page-item.disabled .page-link {
        color: ${brandColors.greyMid};
        background-color: ${brandColors.backgroundWhite}; /* Use white for disabled bg */
        border-color: ${brandColors.greyLight};
    }
`;

const LoadingErrorWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 40vh;
    color: ${brandColors.textSecondary};
    background: ${brandColors.backgroundWhite};
    border-radius: var(--border-radius-lg);
    border: 1px dashed ${brandColors.greyMid};
    margin: 2rem 0;

     .spinner-border {
        width: 3rem;
        height: 3rem;
        color: ${brandColors.secondary};
     }

     p {
        margin-top: 1.5rem;
        font-size: 1.15rem;
        font-weight: 500;
     }

     .text-danger {
         color: #dc3545 !important;
         background: rgba(220, 53, 69, 0.05);
         padding: 1rem;
         border-radius: 8px;
     }
`;

// --- Helper Function for Ratings ---
const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    //const hasHalfStar = rating % 1 !== 0; // Basic check, no half star icon used here yet

    for (let i = 0; i < 5; i++) {
        if (i < fullStars) {
            stars.push(<FiStar key={`star-${i}`} className="filled-star" size="1em"/>);
        } else {
            // Basic implementation: show empty star if not full
            stars.push(<FiStar key={`star-${i}`} className="empty-star" size="1em"/>);
        }
    }
    // You could add more complex logic here for half stars if needed using a different icon
    return stars;
};


// --- Component ---

const Explore = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [wishedCourses, setWishedCourses] = useState([]); // Track wishlisted courses
    const [savingCourseId, setSavingCourseId] = useState(null); // Track the course being saved

    // Course detail modal (mobile tap-to-view)
    const [selectedCourse, setSelectedCourse] = useState(null);
    const isMobile = () => window.innerWidth <= 576;

    // Available platforms state pulled dynamically from database
    const [availablePlatforms, setAvailablePlatforms] = useState([]);

    // Fetch distinct platforms directly from backend database on mount
    useEffect(() => {
        const fetchDatabasePlatforms = async () => {
            try {
                const res = await axios.get(`${BASE_URL}/explore/platforms`);
                if (Array.isArray(res.data?.platforms) && res.data.platforms.length > 0) {
                    setAvailablePlatforms(res.data.platforms.filter(Boolean).sort());
                    return;
                }
            } catch (err) {
                // Fallback to querying explore backend dataset for distinct platforms
            }
            try {
                const res = await axios.get(`${BASE_URL}/explore/filter`, { params: { limit: 500 } });
                const fetched = res.data?.courses || [];
                const dbPlatforms = Array.from(new Set(fetched.map(c => c.platform).filter(Boolean))).sort();
                if (dbPlatforms.length > 0) {
                    setAvailablePlatforms(dbPlatforms);
                }
            } catch (err) {
                console.error("Could not fetch database platforms:", err);
            }
        };
        fetchDatabasePlatforms();
    }, []);

    // Filter input states
    const [level, setLevel] = useState('');
    const [status, setStatus] = useState('');
    const [platform, setPlatform] = useState('');
    const [tagsInput, setTagsInput] = useState('');

    // Sticky navbar & scroll threshold state
    const [isNavHidden, setIsNavHidden] = useState(false);
    const filterRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            // Hide navbar immediately as soon as user begins scrolling down
            if (scrollY > 15) {
                setIsNavHidden(true);
            } else {
                setIsNavHidden(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Applied filters state (includes search term now)
    const [appliedFilters, setAppliedFilters] = useState({
        level: '',
        status: '',
        platform: '',
        tags: '',
        search: '', // Search term handled here
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const ITEMS_PER_PAGE = 12; // 4x3 layout

    // Fetch courses function using actual API call
    const fetchCourses = useCallback(async (page = 1, filters) => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(`${BASE_URL}/explore/filter`, {
                params: {
                    ...filters,
                    page,
                    limit: ITEMS_PER_PAGE,
                },
            });

            const { courses: fetchedCourses, currentPage: fetchedCurrentPage, totalPages: fetchedTotalPages } = response.data;

            setCourses(fetchedCourses || []);
            setCurrentPage(fetchedCurrentPage || 1);
            setTotalPages(fetchedTotalPages || 1);

            // Extract unique platforms directly from database response
            if (Array.isArray(fetchedCourses) && fetchedCourses.length > 0) {
                const extracted = fetchedCourses.map(c => c.platform).filter(Boolean);
                setAvailablePlatforms(prev => Array.from(new Set([...prev, ...extracted])).sort());
            }

        } catch (err) {
            console.error('Error fetching courses:', err);
            if (err.response) {
                console.error("Error response data:", err.response.data);
                console.error("Error response status:", err.response.status);
                setError(`Failed to load courses (Status: ${err.response.status}). Please try again.`);
            } else if (err.request) {
                console.error("Error request:", err.request);
                setError('Failed to load courses. No response received from server. Please check your connection.');
            } else {
                setError('Failed to load courses. An unexpected error occurred.');
            }
            setCourses([]);
            setTotalPages(1);
            setCurrentPage(1);
        } finally {
            setLoading(false);
        }
    }, []); // fetchCourses itself doesn't need dependencies

    // Effect to fetch courses when filters or page change
    useEffect(() => {
        fetchCourses(currentPage, appliedFilters);
    }, [appliedFilters, currentPage, fetchCourses]); // Add fetchCourses here

    // Handle Search from Header Component
    const handleSearch = (searchQuery) => {
        setAppliedFilters(prevFilters => ({
            ...prevFilters,
            search: searchQuery,
        }));
        setCurrentPage(1);
    };

    // Handle filter application from the filter bar (excluding search)
    const handleFilter = () => {
        const newFilters = {
            ...appliedFilters,
            level,
            status,
            platform,
            tags: tagsInput,
        };
        setAppliedFilters(newFilters);
        setCurrentPage(1);
    };

    // Handle Enter key for Tags input only
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.target.id === 'filterTags') {
            setTagsInput(e.target.value); // Ensure state is updated if needed
            handleFilter(); // Apply filters including the tag
        }
    };

     // Handle pagination click
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            setCurrentPage(page);
             // fetchCourses will be triggered by the useEffect watching currentPage
             window.scrollTo(0, 0); // Scroll to top on page change
        }
    };

    // Add course to wishlist (not dashboard enrollment)
    const handleAddCourse = async (course) => {
        setSavingCourseId(course._id);
        try {
            const token = localStorage.getItem("motivar-token");
            if (!token) {
                alert("Please log in to save courses to your wishlist.");
                return;
            }

            await axiosInstance.post(
                '/dashboard/wishlist',
                {
                    courseId: course._id,
                    title: course.title,
                    url: course.url,
                    platform: course.platform,
                    status: course.status,
                    price: course.price,
                    priceUnit: course.priceUnit,
                }
            );

            setWishedCourses((prev) => [...prev, course._id]);
        } catch (err) {
            console.error("Error saving to wishlist:", err);
            alert(err.response?.data?.message || "Failed to save to wishlist. Please try again.");
        } finally {
            setSavingCourseId(null);
        }
    };

    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        // Check if the user is logged in by verifying the token in localStorage
        const token = localStorage.getItem("motivar-token");
        setIsLoggedIn(!!token); // Set to true if token exists, false otherwise
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("motivar-token");
        localStorage.removeItem("motivar-user-role");
        window.location.href = "/user-auth";
    };

     // --- Render Pagination Items (Function remains the same) ---
     const renderPaginationItems = () => {
        let items = [];
        const maxPagesToShow = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        const adjustedStartPage = Math.max(1, endPage - maxPagesToShow + 1);

        items.push(
            <Pagination.Item key="prev" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <FiChevronLeft />
            </Pagination.Item>
        );

        if (adjustedStartPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>{1}</Pagination.Item>);
            if (adjustedStartPage > 2) {
                 items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let number = adjustedStartPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
             if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }

        items.push(
            <Pagination.Item key="next" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                 <FiChevronRight />
            </Pagination.Item>
        );

        return items;
    };

    return (
        <>
            <GlobalStyle />
            <SiteNavbar
                style={{
                    transform: isNavHidden ? 'translateY(-100%)' : 'translateY(0)',
                    opacity: isNavHidden ? 0 : 1,
                    pointerEvents: isNavHidden ? 'none' : 'auto',
                }}
                containerFluid={false}
                containerClassName=""
                leftNavDesktop={
                    <>
                        <NavDropdown title="Programs" id="programs-dropdown">
                            <NavDropdown.Item as={Link} to="/coming-soon">DAP</NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/coming-soon">DigiAccess</NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown title="Community" id="community-dropdown">
                            <NavDropdown.Item as={Link} to="/coming-soon">Find learners near you</NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/coming-soon">Find mentors near you</NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/coming-soon">Join accountability group</NavDropdown.Item>
                        </NavDropdown>
                    </>
                }
                leftNavMobile={
                    <>
                        <NavDropdown title="Programs" id="programs-dropdown-mobile">
                            <NavDropdown.Item as={Link} to="/coming-soon">DAP</NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/coming-soon">DigiAccess</NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown title="Community" id="community-dropdown-mobile">
                            <NavDropdown.Item as={Link} to="/coming-soon">Find learners near you</NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/coming-soon">Find mentors near you</NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/coming-soon">Join accountability group</NavDropdown.Item>
                        </NavDropdown>
                    </>
                }
                rightContent={
                    <div className="d-flex align-items-center" style={{ gap: "0.75rem" }}>
                        {/* Dashboard Link */}
                        {isLoggedIn && (
                            <StyledButton
                                as={Link}
                                to="/dashboard"
                                style={{
                                    border: "none",
                                    color: "#ffffff",
                                    background: "linear-gradient(135deg, #00AA87 0%, #00886C 100%)",
                                    borderRadius: "50px",
                                    fontWeight: 600,
                                    padding: "0.45rem 1.25rem",
                                    textDecoration: "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.4rem",
                                    boxShadow: "none",
                                }}
                            >
                                <FiGrid size={15} /> Dashboard
                            </StyledButton>
                        )}

                        {/* Dynamic Button: Logout or Sign In */}
                        {isLoggedIn ? (
                            <StyledButton
                                variant="outline-danger"
                                onClick={handleLogout}
                                style={{
                                    color: "#dc2626",
                                    border: "1px solid #fca5a5",
                                    backgroundColor: "#fef2f2",
                                    borderRadius: "50px",
                                    fontWeight: 600,
                                    padding: "0.45rem 1.25rem",
                                    textDecoration: "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.4rem",
                                }}
                            >
                                <FiLogOut size={15} /> Logout
                            </StyledButton>
                        ) : (
                            <StyledButton
                            as={Link}
                            to="/user-auth"
                            variant="outline-primary"
                            style={{
                                border: "1px solid #59b49a",
                                color: "#59b49a",
                                background: "#fff",
                                borderRadius: "16px",
                                fontWeight: 600,
                                minWidth: "100px",
                                padding: "0.4rem 1.2rem",
                            }}
                            >
                            <FiUser /> Sign In
                            </StyledButton>
                        )}
                    </div>
                }
            />
             {/* New Header Section */}
            <ExploreHeader>
                 <h1>What do you want to learn today?</h1>
                 <p>
                    Discover thousands of online courses from top providers. Learn new skills, advance your career, or explore a passion.
                 </p>
                 {/* Pass handleSearch and potentially the current search query */}
                 <SearchAutocomplete onSearch={handleSearch} initialQuery={appliedFilters.search} />
            </ExploreHeader>

            <ExploreContainer>
                 {/* Filters */}
                <FilterWrapper ref={filterRef} isNavHidden={isNavHidden}>
                    {/* Reduced columns as Search is removed */}
                    <Row className="g-3 align-items-end">
                        <Col md={6} lg={2}> {/* Level */}
                            <Form.Group controlId="filterLevel">
                                <Form.Label>Level</Form.Label>
                                <StyledFormSelect value={level} onChange={(e) => setLevel(e.target.value)}>
                                    <option value="">All Levels</option>
                                    <option value="Beginner">Beginner</option>
                                    <option value="Intermediate">Intermediate</option>
                                    <option value="Advanced">Advanced</option>
                                    <option value="All">All (Specific)</option>
                                </StyledFormSelect>
                            </Form.Group>
                        </Col>
                        <Col md={6} lg={2}> {/* Status */}
                             <Form.Group controlId="filterStatus">
                                <Form.Label>Status</Form.Label>
                                <StyledFormSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                                    <option value="">All Status</option>
                                    <option value="Free">Free</option>
                                    <option value="Paid">Paid</option>
                                </StyledFormSelect>
                            </Form.Group>
                        </Col>
                         <Col md={6} lg={3}> {/* Platform */}
                            <Form.Group controlId="filterPlatform">
                                <Form.Label>Platform</Form.Label>
                                <StyledFormControl
                                    type="text"
                                    placeholder="Select or type platform..."
                                    value={platform}
                                    onChange={(e) => setPlatform(e.target.value)}
                                    list="platform-suggestions"
                                />
                                <datalist id="platform-suggestions">
                                    {availablePlatforms.map(p => (
                                        <option key={p} value={p} />
                                    ))}
                                </datalist>
                            </Form.Group>
                        </Col>
                        <Col md={6} lg={3}> {/* Tags */}
                            <Form.Group controlId="filterTags">
                                <Form.Label>Tags / Keywords</Form.Label>
                                <StyledFormControl
                                    type="text"
                                    placeholder="e.g., react, python"
                                    value={tagsInput}
                                    onChange={(e) => setTagsInput(e.target.value)}
                                    onKeyPress={handleKeyPress} // Trigger filter on Enter
                                    list="tag-suggestions" // Link to datalist
                                />
                                {/* Basic Datalist for suggestions - replace with better component later */}
                                <datalist id="tag-suggestions">
                                    <option value="Marketing" />
                                    <option value="Python Programming" />
                                    <option value="Data Analysis" />
                                    <option value="Machine Learning" />
                                    <option value="DevOps" />
                                    {/* Add more common tags or fetch dynamically */}
                                </datalist>
                            </Form.Group>
                        </Col>
                        <Col md={12} lg={2}> {/* Apply Button - takes full width on medium */}
                             <StyledButton variant="primary" onClick={handleFilter} className="w-100">
                                <FiFilter /> Apply Filters
                            </StyledButton>
                        </Col>
                    </Row>
                </FilterWrapper>


                {/* Course List Area */}
                 {loading ? (
                    <LoadingErrorWrapper>
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p>Loading courses...</p>
                    </LoadingErrorWrapper>
                ) : error ? (
                     <LoadingErrorWrapper>
                        <p className="text-danger">{error}</p>
                     </LoadingErrorWrapper>
                 ) : courses.length > 0 ? (
                    <>
                        {/* Course Grid - Updated Column Count */}
                        <Row xs={1} sm={2} md={3} lg={4} className="g-4">
                            {courses.map((course) => {
                                const imageUrl = getCourseImage(course.title, course.description, course.tags || []);
                                const isWished = wishedCourses.includes(course._id);
                                return (
                                <Col key={course._id}>
                                    <CourseCardWrapper
                                        onClick={() => { if (isMobile()) setSelectedCourse(course); }}
                                        style={{ cursor: isMobile() ? 'pointer' : 'default' }}
                                    >
                                        <CardImagePlaceholder className="card-img-placeholder">
                                            {imageUrl ? (
                                                <img src={imageUrl} alt={course.title} />
                                            ) : (
                                                <FiImage /> // Fallback icon
                                            )}
                                        </CardImagePlaceholder>
                                        <CardBodyStyled>
                                            <CardTitleStyled>{course.title}</CardTitleStyled>

                                            {/* Meta Info: Ratings and Price */}
                                            <CardMetaInfo>
                                                 {/* Display Ratings if available */}
                                                <span>
                                                    {typeof course.ratings === 'number' ? (
                                                        <RatingStars>
                                                            {renderStars(course.ratings)}
                                                            <span style={{ marginLeft: '0.3rem', color: brandColors.textSecondary }}>
                                                                ({course.ratings.toFixed(1)})
                                                            </span>
                                                        </RatingStars>
                                                    ) : (
                                                        // Optional: Show placeholder if no rating
                                                         <span style={{ color: brandColors.greyMid }}>No rating</span>
                                                    )}
                                                </span>

                                                 {/* Display Price */}
                                                <PriceInfo className={course.status === 'Free' ? 'free' : ''}>
                                                    {course.status === 'Free' ? (
                                                        <>Free</>
                                                    ) : typeof course.price === 'number' ? (
                                                         `${course.priceUnit || ''}${course.price}`
                                                    ) : (
                                                         'Paid' // Fallback if price number isn't available
                                                    )}
                                                </PriceInfo>
                                            </CardMetaInfo>


                                            <CardDescription>{course.description}</CardDescription>

                                            <BadgeGroup>
                                                {course.level && <StyledBadge pill bg="" className="badge-level"><FiBarChart2 /> {course.level}</StyledBadge>}
                                                {course.platform && <StyledBadge pill bg="" className="badge-platform">{course.platform}</StyledBadge>}
                                                {/* Render first 2 tags */}
                                                 {course.tags && course.tags.length > 0 && course.tags.slice(0, 2).map(tag => (
                                                     <StyledBadge key={tag} pill bg="" className="badge-tag"><FiTag /> {tag}</StyledBadge>
                                                 ))}
                                            </BadgeGroup>

                                             <CardFooterStyled>
                                                <StyledButton
                                                    variant="outline-primary"
                                                    size="sm"
                                                    href={course.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-100" // Make button full width in footer
                                                >
                                                    <FiExternalLink /> View Course
                                                </StyledButton>
                                                <StyledButton
                                                    variant={isWished ? "outline-success" : "outline-primary"}
                                                    size="sm"
                                                    onClick={() => handleAddCourse(course)}
                                                    disabled={isWished || savingCourseId === course._id}
                                                    className="mt-2 w-100"
                                                >
                                                    {isWished ? (
                                                        <FiCheckCircle />
                                                    ) : savingCourseId === course._id ? (
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                    ) : (
                                                        <FiHeart />
                                                    )}{" "}
                                                    {isWished ? "Wishlisted" : savingCourseId === course._id ? "Saving..." : "Save to Wishlist"}
                                                </StyledButton>
                                            </CardFooterStyled>

                                        </CardBodyStyled>
                                    </CourseCardWrapper>
                                </Col>
                            )})}
                        </Row>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <PaginationWrapper>
                                <Pagination>{renderPaginationItems()}</Pagination>
                            </PaginationWrapper>
                        )}
                    </>
                 ) : (
                     <LoadingErrorWrapper>
                         <p>No courses found matching your criteria. Try adjusting your filters or search term.</p>
                     </LoadingErrorWrapper>
                 )}
            </ExploreContainer>
            {/* Footer */}
            <AppFooter />

            {/* ── Course Detail Modal (mobile tap-to-view) ── */}
            {selectedCourse && (
                <div
                    onClick={() => setSelectedCourse(null)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 1050,
                        background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'flex-end',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff',
                            borderRadius: '20px 20px 0 0',
                            width: '100%',
                            maxHeight: '88vh',
                            overflowY: 'auto',
                            padding: '24px 20px 32px',
                            boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
                        }}
                    >
                        {/* Drag handle */}
                        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#e2e8f0', margin: '0 auto 20px' }} />

                        {/* Course image */}
                        {getCourseImage(selectedCourse.title, selectedCourse.description, selectedCourse.tags || []) && (
                            <img
                                src={getCourseImage(selectedCourse.title, selectedCourse.description, selectedCourse.tags || [])}
                                alt={selectedCourse.title}
                                style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 12, marginBottom: 16 }}
                            />
                        )}

                        {/* Badges */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                            {selectedCourse.platform && (
                                <span style={{ background: 'rgba(255,122,89,0.1)', color: '#ff7a59', fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 10px' }}>
                                    {selectedCourse.platform}
                                </span>
                            )}
                            {selectedCourse.level && (
                                <span style={{ background: 'rgba(0,170,135,0.1)', color: '#00AA87', fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 10px' }}>
                                    {selectedCourse.level}
                                </span>
                            )}
                            {selectedCourse.status === 'Free' && (
                                <span style={{ background: 'rgba(40,167,69,0.1)', color: '#28a745', fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 10px' }}>
                                    Free
                                </span>
                            )}
                        </div>

                        {/* Title */}
                        <h5 style={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', marginBottom: 8 }}>
                            {selectedCourse.title}
                        </h5>

                        {/* Description */}
                        <p style={{ fontSize: '0.87rem', color: '#64748b', lineHeight: 1.6, marginBottom: 16 }}>
                            {selectedCourse.description || 'No description available.'}
                        </p>

                        {/* Price */}
                        {selectedCourse.status !== 'Free' && typeof selectedCourse.price === 'number' && (
                            <p style={{ fontWeight: 700, color: '#00AA87', fontSize: '1rem', marginBottom: 16 }}>
                                {selectedCourse.priceUnit || ''}{selectedCourse.price}
                            </p>
                        )}

                        {/* Action buttons */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <a
                                href={selectedCourse.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                    background: 'linear-gradient(135deg, #00AA87 0%, #00886C 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                                    padding: '13px 0', borderRadius: 12, textDecoration: 'none',
                                    boxShadow: '0 4px 14px rgba(0,170,135,0.3)',
                                }}
                            >
                                <FiExternalLink size={16} /> View Course
                            </a>
                            <button
                                onClick={() => { handleAddCourse(selectedCourse); }}
                                disabled={wishedCourses.includes(selectedCourse._id) || savingCourseId === selectedCourse._id}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                    background: wishedCourses.includes(selectedCourse._id) ? '#f0fdf4' : '#fff',
                                    color: wishedCourses.includes(selectedCourse._id) ? '#16a34a' : '#00AA87',
                                    border: `2px solid ${wishedCourses.includes(selectedCourse._id) ? '#bbf7d0' : '#00AA87'}`,
                                    fontWeight: 700, fontSize: '0.95rem',
                                    padding: '12px 0', borderRadius: 12, cursor: 'pointer',
                                }}
                            >
                                {wishedCourses.includes(selectedCourse._id)
                                    ? <><FiCheckCircle size={16} /> Wishlisted</>
                                    : savingCourseId === selectedCourse._id
                                        ? <><Spinner as="span" animation="border" size="sm" /> Saving...</>
                                        : <><FiHeart size={16} /> Save to Wishlist</>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Explore;